import { useMemo } from "react";
import { TrendingUp, TrendingDown } from "lucide-react";
import { useCryptoPrices, type CoinPrice } from "@/hooks/useCryptoPrices";

/**
 * 顶栏下方行情条：TSK / BTC / ETH，marquee 滚动（80s 一圈，与 dapp 一致）
 */
function formatPrice(price: number): string {
  if (price >= 1000)
    return price.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  if (price >= 1) return price.toFixed(2);
  return Number(price.toFixed(4)).toString();
}

function CoinItem({ coin }: { coin: CoinPrice }) {
  const isUp = coin.change24h >= 0;

  return (
    <div className="flex items-center gap-1.5 px-3 py-0.5 shrink-0">
      <span
        className="text-[10px] font-bold tracking-wide"
        style={{ color: "rgba(255,255,255,0.5)" }}
      >
        {coin.symbol}
      </span>
      <span
        className="text-[10px] font-semibold tabular-nums"
        style={{ color: "rgba(255,255,255,0.8)" }}
      >
        ${formatPrice(coin.price)}
      </span>
      <span
        className="flex items-center gap-0.5 text-[9px] tabular-nums font-medium"
        style={{
          color: isUp ? "#22c55e" : "#ef4444",
        }}
      >
        {isUp ? <TrendingUp size={8} /> : <TrendingDown size={8} />}
        {isUp ? "+" : ""}
        {coin.change24h.toFixed(2)}%
      </span>
      <span
        className="text-[6px] ml-1"
        style={{ color: "rgba(255,255,255,0.15)" }}
      >
        ●
      </span>
    </div>
  );
}

export default function PriceTicker() {
  const coins = useCryptoPrices();

  const repeatedCoins = useMemo(
    () => [...coins, ...coins, ...coins, ...coins],
    [coins]
  );

  return (
    <div
      className="w-full overflow-hidden"
      style={{
        background: "rgba(10,12,18,0.85)",
        backdropFilter: "blur(8px)",
        borderBottom: "1px solid rgba(255,255,255,0.04)",
      }}
    >
      <div className="ticker-scroll flex items-center py-1.5">
        <div className="ticker-track flex items-center shrink-0">
          {repeatedCoins.map((coin, i) => (
            <CoinItem key={`a-${coin.symbol}-${i}`} coin={coin} />
          ))}
        </div>
        <div className="ticker-track flex items-center shrink-0" aria-hidden>
          {repeatedCoins.map((coin, i) => (
            <CoinItem key={`b-${coin.symbol}-${i}`} coin={coin} />
          ))}
        </div>
      </div>

      <style>{`
        .ticker-scroll {
          display: flex;
          width: max-content;
        }
        .ticker-track {
          animation: ticker-slide 80s linear infinite;
        }
        @keyframes ticker-slide {
          0% { transform: translateX(0); }
          100% { transform: translateX(-100%); }
        }
      `}</style>
    </div>
  );
}
